import { ReactNode } from "react";
import { useClient } from "@/contexts/ClientContext";
import { Loader2 } from "lucide-react";

interface PlayerAccessGuardProps {
  children: ReactNode;
}

// Canonical access rule: only status in ('trialing','active') grants public access.
// Anything else ('pending_payment','trial_expired','suspended','inactive',
// archived, or unknown) freezes the public player surface (landing, games,
// vouchers, menu). This guard never blocks admin routes.
const ALLOWED_STATUSES = new Set(["trialing", "active"]);

export const PlayerAccessGuard = ({ children }: PlayerAccessGuardProps) => {
  const { client, loading } = useClient();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // No client resolved yet: let downstream pages handle (they show their own
  // "not found" copy). We only freeze when we DO have a client and its status
  // is not open.
  if (!client) {
    return <>{children}</>;
  }

  const status = (client.status ?? "").toString();
  const isFrozen = client.archived_at != null || !ALLOWED_STATUSES.has(status);

  if (!isFrozen) {
    return <>{children}</>;
  }

  const logoUrl = client.logo_url;
  const name = client.name || "ce commerçant";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-sm border border-slate-200 px-8 py-10">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={name}
            className="h-16 w-16 mx-auto mb-6 rounded-xl object-cover"
          />
        ) : (
          <div className="h-16 w-16 mx-auto mb-6 rounded-xl bg-gradient-to-br from-[#1B6FC2] via-[#1E9DAA] to-[#2EAE6D]" />
        )}
        <h1 className="text-xl font-semibold text-slate-900 mb-3">
          Ce service est momentanément indisponible
        </h1>
        <p className="text-sm text-slate-600 leading-relaxed">
          L'espace de jeu de {name} n'est pas accessible pour le moment.
          Revenez un peu plus tard, ou demandez à l'équipe sur place.
        </p>
        <p className="text-xs text-slate-400 mt-8">Propulsé par BoumRank</p>
      </div>
    </div>
  );
};

export default PlayerAccessGuard;
